// /settings ページ — Stage 3B 路由整理後的真实化版本 + Stage 3C 权限调整
//
// 权限策略(Stage 3C):
// - /settings 页面本身:所有已登录用户都可以打开
// - WordPress 接続設定 Card(外部連携 / API key 相关):仅 system_admin 可见
// - 其他 4 个 mock Card(Gmail / OpenAI / GA4 / Google ビジネス):仅 system_admin 可见
// - editor / viewer 看到空状态「現在設定できる項目はありません」
//
// 范围(Stage 3B 真实化):
// - WordPressSettingsCard 接真实 API(siteApi)
// - 其他 4 个 mock card 是 Stage 1 留下的,1.0 後置范围,不在 Stage 3B/3C 内

import { ReactNode, useCallback, useEffect, useState } from "react";
import {
  Sparkles,
  BarChart3,
  MapPin,
  Save,
  Trash2,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Clock,
  Instagram,
  Languages,
  Pencil,
  Database,
} from "lucide-react";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { ApiError } from "../../api/client";
import {
  aiCredentialApi,
  type AiCredential,
  type AiCredentialTestResult,
} from "../../api/aiCredential";
import {
  siteApi,
  type WordPressConnection,
  type WordPressTestResult,
} from "../../api/site";
import { googleBusinessApi, type GbpSettings } from "../../api/googleBusiness";
import { useAuth } from "../../auth/AuthProvider";
import { useUiLanguage } from "../../i18n/UiLanguageProvider";
import { formatJstDateTime } from "../../utils/dateTime";
import { MailSettingsCard } from "./MailSettingsCard";
import { SessionsPanel } from "./SessionsPanel";
import { GoogleAnalyticsSettingsCardInner } from "./GoogleAnalyticsSettingsCard";
import { MetaSettingsCardInner } from "./MetaSettingsCard";
import { PasswordChangeCard } from "./PasswordChangeCard";

const SHOW_LANGUAGE_SETTING = true;

const AI_PROVIDER_PRESETS = [
  {
    id: "openai",
    label: "ChatGPT / OpenAI",
    baseUrl: "https://api.openai.com",
    model: "gpt-4.1-mini",
  },
  {
    id: "minimax",
    label: "MiniMax",
    baseUrl: "https://api.minimax.chat",
    model: "MiniMax-M3",
  },
  {
    id: "other",
    label: "Other",
    baseUrl: "",
    model: "",
  },
] as const;

type AiProviderPresetId = (typeof AI_PROVIDER_PRESETS)[number]["id"];

const EMBEDDING_PROVIDER_PRESETS = [
  {
    id: "voyage",
    label: "Voyage AI",
    baseUrl: "https://api.voyageai.com/v1",
    model: "voyage-3-lite",
  },
  {
    id: "other",
    label: "Other",
    baseUrl: "",
    model: "",
  },
] as const;

type EmbeddingProviderPresetId = (typeof EMBEDDING_PROVIDER_PRESETS)[number]["id"];

type SettingCardProps = {
  icon: ReactNode;
  title: string;
  subtitle: string;
  status: "connected" | "warning";
  children: ReactNode;
  /**
   * 是否显示通用 footer 按钮(保存 / テスト接続 / 再同期)。
   * 默认 true。仅当卡片自己处理这些操作时设 false(例如 MetaSettingsCard 自己有完整按钮)。
   */
  showDefaultActions?: boolean;
};

export const SettingCard = ({
  icon,
  title,
  subtitle,
  status,
  children,
  showDefaultActions = true,
}: SettingCardProps) => {
  const { pick } = useUiLanguage();

  return (
    <Card>
      <div className="settings-card-head">
        <div className="settings-card-title-row">
          <div className="settings-card-icon">{icon}</div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>{title}</div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{subtitle}</div>
          </div>
        </div>
        {status === "connected" ? (
          <Badge color="green" dot>
            <CheckCircle2 size={11} /> {pick("接続中", "Connected")}
          </Badge>
        ) : (
          <Badge color="yellow" dot>
            <AlertCircle size={11} /> {pick("要確認", "Needs review")}
          </Badge>
        )}
      </div>

      {children}

      {showDefaultActions && (
        <>
          <hr className="divider" />

          <div className="row gap-8">
            <Button>{pick("保存", "Save")}</Button>
            <Button variant="secondary">{pick("テスト接続", "Test connection")}</Button>
            <Button variant="ghost" iconLeft={<RefreshCw size={13} />}>
              {pick("再同期", "Resync")}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
};

function LanguageSettingsCard() {
  const { language, setLanguage, pick } = useUiLanguage();

  if (!SHOW_LANGUAGE_SETTING) return null;

  return (
    <SettingCard
      icon={<Languages size={20} />}
      title={pick("表示言語", "Display Language")}
      subtitle={pick("Instagram審査用の一時表示切替", "Temporary language toggle for Instagram review")}
      status="connected"
      showDefaultActions={false}
    >
      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.7 }}>
          {pick(
            "現在は表示文言だけを日本語 / 英語で切り替えます。将来この設定は非表示にできます。",
            "This currently switches visible UI copy between Japanese and English only. It can be hidden later.",
          )}
        </div>
        <div className="row gap-8">
          <Button
            variant={language === "ja" ? "primary" : "secondary"}
            onClick={() => setLanguage("ja")}
          >
            日本語
          </Button>
          <Button
            variant={language === "en" ? "primary" : "secondary"}
            onClick={() => setLanguage("en")}
          >
            English
          </Button>
        </div>
      </div>
    </SettingCard>
  );
}

// ============================================================================
// WordPress 接続設定 — Stage 3B 真实化
// ============================================================================

function statusToBadgeColor(
  status: string | null | undefined,
): "green" | "red" | "yellow" | "gray" {
  if (status === "connected") return "green";
  if (status === "auth_failed") return "red";
  if (status === "not_found") return "yellow";
  if (status === "timeout") return "yellow";
  if (status === "error") return "red";
  return "gray";
}

function statusLabelJa(status: string | null | undefined): string {
  if (status === "connected") return "接続中";
  if (status === "auth_failed") return "認証失敗";
  if (status === "not_found") return "未検出";
  if (status === "timeout") return "タイムアウト";
  if (status === "error") return "エラー";
  return status ?? "";
}

function WordPressSettingsCard() {
  const [conn, setConn] = useState<WordPressConnection | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const [siteUrl, setSiteUrl] = useState("");
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [applicationPassword, setApplicationPassword] = useState("");

  const [busy, setBusy] = useState<"load" | "save" | "test" | "delete" | null>(null);
  const [testResult, setTestResult] = useState<WordPressTestResult | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setBusy("load");
    setLoadError(null);
    setPageError(null);
    try {
      const data = await siteApi.getConnection();
      setConn(data);
      setEditing(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setConn(null);
        setEditing(true);
      } else if (e instanceof ApiError) {
        setLoadError(e.message);
        setConn(null);
        setEditing(true);
      } else {
        setLoadError(String(e));
        setConn(null);
        setEditing(true);
      }
    } finally {
      setLoaded(true);
      setBusy(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleSave() {
    const nextSiteUrl = formSiteUrl.trim();
    const nextUsername = formUsername.trim();
    if (!nextSiteUrl || !nextUsername || !applicationPassword) {
      window.alert("site_url / username / application_password はすべて必須です。");
      return;
    }
    if (!nextSiteUrl.startsWith("https://")) {
      window.alert("site_url は https:// 必須です。");
      return;
    }
    setBusy("save");
    setPageError(null);
    setTestResult(null);
    try {
      const saved = await siteApi.upsertConnection({
        site_url: nextSiteUrl,
        username: nextUsername,
        application_password: applicationPassword,
        display_name: displayName.trim() || null,
      });
      setConn(saved);
      setEditing(false);
      // 关键:application_password 输入框保存后清空,不回显
      setApplicationPassword("");
      window.alert("接続設定を保存しました。");
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      window.alert(`保存に失敗しました: ${msg}`);
    } finally {
      setBusy(null);
    }
  }

  async function handleTest() {
    const nextSiteUrl = formSiteUrl.trim();
    const nextUsername = formUsername.trim();
    if (!nextSiteUrl || !nextUsername) {
      window.alert("site_url / username は必須です。");
      return;
    }
    setBusy("test");
    setPageError(null);
    setTestResult(null);
    try {
      const body: Parameters<typeof siteApi.testConnection>[0] = {
        site_url: nextSiteUrl,
        username: nextUsername,
      };
      if (applicationPassword) {
        body.application_password = applicationPassword;
      }
      const result = await siteApi.testConnection(body);
      setTestResult(result);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      window.alert(`接続テストに失敗しました: ${msg}`);
    } finally {
      setBusy(null);
    }
  }

  async function handleDelete() {
    if (
      !window.confirm(
        "WordPress 接続設定を削除しますか?\n\n※ パスワード自体は DB に残るので再保存は可能ですが、削除すると blog リストなどの WordPress 連携機能が止まります。",
      )
    ) {
      return;
    }
    setBusy("delete");
    setPageError(null);
    try {
      await siteApi.deleteConnection();
      setConn(null);
      setEditing(true);
      setTestResult(null);
      window.alert("接続設定を削除しました。");
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      window.alert(`削除に失敗しました: ${msg}`);
    } finally {
      setBusy(null);
    }
  }

  // 卡片头部状态:
  // - 有 connection 且 last_test_status=connected → connected
  // - 有 connection 但无测试 / 失败 → warning
  // - 无 connection → warning(要確認)
  const cardStatus: "connected" | "warning" =
    conn && conn.last_test_status === "connected" ? "connected" : "warning";

  // 表单默认值:
  // - 有 connection 时:显示已保存的 site_url/username/display_name
  // - password 留空(安全性)
  const formSiteUrl = siteUrl || conn?.site_url || "";
  const formUsername = username || conn?.username || "";
  const formDisplayName = displayName || conn?.display_name || "";
  const isEditing = !conn || editing;

  function startEditing() {
    setSiteUrl(conn?.site_url || "");
    setUsername(conn?.username || "");
    setDisplayName(conn?.display_name || "");
    setApplicationPassword("");
    setTestResult(null);
    setEditing(true);
  }

  function cancelEditing() {
    setSiteUrl("");
    setUsername("");
    setDisplayName("");
    setApplicationPassword("");
    setTestResult(null);
    setEditing(false);
  }

  return (
    <Card>
      <div className="settings-card-head">
        <div className="settings-card-title-row">
          <div className="settings-card-icon">
            <Save size={20} />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
              WordPress 接続設定
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              ブログ記事の取得元 WordPress サイトの接続情報
            </div>
          </div>
        </div>
        {cardStatus === "connected" ? (
          <Badge color="green" dot>
            <CheckCircle2 size={11} /> 接続中
          </Badge>
        ) : (
          <Badge color="yellow" dot>
            <AlertCircle size={11} /> {conn ? "要確認" : "未設定"}
          </Badge>
        )}
      </div>

      {(loadError || pageError) && (
        <div
          style={{
            padding: "10px 12px",
            background: "#fef2f2",
            color: "#b91c1c",
            borderRadius: 8,
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          <AlertCircle size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />
          {loadError || pageError}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {conn && !isEditing ? (
          <div
            style={{
              display: "grid",
              gap: 14,
              padding: "16px",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              color: "#374151",
            }}
          >
            <div className="row gap-12" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontSize: 13, color: "#64748b", fontWeight: 700, marginBottom: 4 }}>
                  接続先サイト
                </div>
                <div style={{ fontSize: 16, color: "#111827", fontWeight: 700 }}>
                  {conn.display_name || conn.site_url}
                </div>
                <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
                  {conn.site_url}
                </div>
              </div>
              <Badge color={conn.last_test_status === "connected" ? "green" : statusToBadgeColor(conn.last_test_status)} dot>
                {conn.last_test_status ? statusLabelJa(conn.last_test_status) : "設定済み"}
              </Badge>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 10,
              }}
            >
              <div style={{ padding: 12, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10 }}>
                <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>ユーザー</div>
                <div style={{ marginTop: 4, fontWeight: 700, color: "#111827" }}>{conn.username}</div>
              </div>
              <div style={{ padding: 12, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10 }}>
                <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>認証情報</div>
                <div style={{ marginTop: 4, fontWeight: 700, color: conn.has_application_password ? "#166534" : "#991b1b" }}>
                  {conn.application_password_hint ?? (conn.has_application_password ? "設定済み" : "未設定")}
                </div>
              </div>
              <div style={{ padding: 12, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10 }}>
                <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>最終確認</div>
                <div style={{ marginTop: 4, fontWeight: 700, color: "#111827" }}>
                  {conn.last_tested_at ? formatJstDateTime(conn.last_tested_at) : "未実施"}
                </div>
              </div>
            </div>
            {conn.last_test_message ? (
              <div style={{ color: "#64748b", fontSize: 12 }}>
                {conn.last_test_message}
              </div>
            ) : null}
          </div>
        ) : (
          <>
            <div className="field">
              <label className="field-label">
                サイト URL <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <input
                className="input"
                type="url"
                value={formSiteUrl}
                onChange={(e) => setSiteUrl(e.target.value)}
                placeholder="https://example.com"
                disabled={busy !== null}
              />
            </div>

            <div className="field">
              <label className="field-label">
                ユーザー名 <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <input
                className="input"
                type="text"
                value={formUsername}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="wp_user"
                disabled={busy !== null}
              />
            </div>

            <div className="field">
              <label className="field-label">
                Application Password <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <input
                className="input"
                type="password"
                value={applicationPassword}
                onChange={(e) => setApplicationPassword(e.target.value)}
                placeholder="WordPress の Application Password"
                disabled={busy !== null}
                autoComplete="off"
              />
              <span className="field-hint">
                保存後は表示されません。WordPress 管理画面の「ユーザー → プロフィール」で発行できます。
              </span>
            </div>

            <div className="field">
              <label className="field-label">表示名(任意)</label>
              <input
                className="input"
                type="text"
                value={formDisplayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="例: 本番ブログ"
                disabled={busy !== null}
              />
            </div>
          </>
        )}

        {testResult && (
          <div
            style={{
              padding: "10px 12px",
              background: testResult.status === "connected" ? "#f0fdf4" : "#fef2f2",
              border: `1px solid ${testResult.status === "connected" ? "#bbf7d0" : "#fecaca"}`,
              borderRadius: 8,
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              fontSize: 13,
            }}
          >
            {testResult.status === "connected" ? (
              <CheckCircle2 size={16} color="#16a34a" style={{ flexShrink: 0, marginTop: 2 }} />
            ) : (
              <XCircle size={16} color="#dc2626" style={{ flexShrink: 0, marginTop: 2 }} />
            )}
            <div style={{ flex: 1 }}>
              <div className="row gap-8" style={{ marginBottom: 4 }}>
                <Badge color={statusToBadgeColor(testResult.status)} dot>
                  {testResult.status}
                </Badge>
                <Badge color="gray">{testResult.mode}</Badge>
              </div>
              <div style={{ color: "#374151" }}>{testResult.message}</div>
              {testResult.wp_user && (
                <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
                  WordPress ユーザー: {testResult.wp_user.name} (id={testResult.wp_user.id})
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <hr className="divider" />

      <div className="row gap-8">
        {isEditing ? (
          <Button
            iconLeft={<Save size={13} />}
            onClick={handleSave}
            disabled={busy !== null || !loaded}
          >
            保存
          </Button>
        ) : (
          <Button
            iconLeft={<Pencil size={13} />}
            onClick={startEditing}
            disabled={busy !== null || !loaded}
          >
            変更
          </Button>
        )}
        {conn && isEditing ? (
          <Button variant="secondary" onClick={cancelEditing} disabled={busy !== null}>
            キャンセル
          </Button>
        ) : null}
        <Button variant="secondary" onClick={handleTest} disabled={busy !== null || !loaded}>
          接続テスト
        </Button>
        <Button
          variant="ghost"
          iconLeft={<RefreshCw size={13} />}
          onClick={load}
          disabled={busy !== null}
        >
          再読み込み
        </Button>
        {conn && (
          <Button
            variant="ghost"
            iconLeft={<Trash2 size={13} />}
            onClick={handleDelete}
            disabled={busy !== null}
          >
            削除
          </Button>
        )}
      </div>
    </Card>
  );
}

/**
 * 未実装機能カード用の簡素な占位表示。
 * Stage 3C 收尾修正:fake 接続中 / fake API key / fake service account を削除し、
 * 今後対応予定の案内のみを表示する。
 */
function NotYetImplemented({ description }: { description: string }) {
  return (
    <div
      style={{
        padding: "16px",
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        color: "#374151",
      }}
    >
      <Clock size={18} color="#6b7280" style={{ flexShrink: 0, marginTop: 2 }} />
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", marginBottom: 4 }}>
          本機能は現在未実装です(今後対応予定)
        </div>
        <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.6 }}>{description}</div>
      </div>
    </div>
  );
}

function GoogleBusinessSettingsCardInner() {
  const [data, setData] = useState<GbpSettings | null>(null);
  const [busy, setBusy] = useState<"load" | "save" | "connect" | "delete" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const enabledIds = data?.locations.filter((l) => l.isEnabled).map((l) => l.id) || [];
  const defaultId = data?.locations.find((l) => l.isDefault)?.id || null;

  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const oauthError = qs.get("gbp_error");
    if (oauthError) {
      setError(`GBP OAuth failed: ${oauthError}`);
    }
  }, []);

  const load = useCallback(async () => {
    setBusy("load");
    setError(null);
    try {
      setData(await googleBusinessApi.getSettings());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function connect() {
    setBusy("connect");
    setError(null);
    try {
      const result = await googleBusinessApi.getAuthUrl();
      window.location.href = result.url;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function save(nextEnabledIds: string[], nextDefaultId: string | null) {
    setBusy("save");
    setError(null);
    try {
      setData(await googleBusinessApi.updateLocationSettings({
        enabledLocationIds: nextEnabledIds,
        defaultLocationId: nextDefaultId,
      }));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function disconnect() {
    if (!window.confirm("Google Business Profile connection を削除しますか?")) return;
    setBusy("delete");
    setError(null);
    try {
      await googleBusinessApi.deleteConnection();
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(null);
    }
  }

  async function createDemoData() {
    setBusy("load");
    setError(null);
    try {
      await googleBusinessApi.getLocations();
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  if (!data) {
    return <NotYetImplemented description={busy === "load" ? "Google Business Profile 設定を読み込み中です。" : "Google Business Profile 設定を読み込めませんでした。"} />;
  }

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {error && (
        <div style={{ padding: 10, border: "1px solid #fecaca", background: "#fef2f2", color: "#991b1b", borderRadius: 8, fontSize: 13 }}>
          {error}
        </div>
      )}
      <div style={{ display: "grid", gap: 10, padding: 14, border: "1px solid #e2e8f0", borderRadius: 8, background: "#f8fafc" }}>
        <div className="row-between">
          <div>
            <div style={{ fontWeight: 800, color: "#111827" }}>
              {data.connection ? data.connection.googleEmail : "Not connected"}
            </div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>
              {data.configured ? "OAuth configured" : "OAuth env is not configured. Dev demo data is shown."}
            </div>
          </div>
          <Badge color={data.connection ? "green" : "yellow"} dot>
            {data.connection ? data.connection.status : "未接続"}
          </Badge>
        </div>
        {!data.configured && (
          <div style={{ fontSize: 12, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "9px 10px" }}>
            Google OAuth env is not configured, so real Google connection is unavailable in this environment.
          </div>
        )}
        <div className="row gap-8">
          {data.configured && (
            <Button iconLeft={<MapPin size={13} />} onClick={connect} disabled={busy !== null}>
              {busy === "connect" ? "接続中..." : "Google で接続"}
            </Button>
          )}
          <Button variant="secondary" iconLeft={<RefreshCw size={13} />} onClick={load} disabled={busy !== null}>
            {busy === "load" ? "読み込み中..." : "再読み込み"}
          </Button>
          {!data.connection && !data.configured && (
            <Button variant="secondary" onClick={createDemoData} disabled={busy !== null}>
              Demo data
            </Button>
          )}
          {data.connection && (
            <Button variant="ghost" iconLeft={<Trash2 size={13} />} onClick={disconnect} disabled={busy !== null}>
              {busy === "delete" ? "削除中..." : "削除"}
            </Button>
          )}
        </div>
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: "#334155" }}>Enabled Stores / Default Store</div>
        {data.locations.map((loc) => {
          const checked = enabledIds.includes(loc.id);
          return (
            <div key={loc.id} className="row-between" style={{ padding: 12, border: "1px solid #e2e8f0", borderRadius: 8 }}>
              <label className="row gap-8" style={{ fontWeight: 700, color: "#111827" }}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    const next = e.target.checked ? [...enabledIds, loc.id] : enabledIds.filter((id) => id !== loc.id);
                    save(next, defaultId === loc.id && !e.target.checked ? next[0] || null : defaultId);
                  }}
                  disabled={busy !== null}
                />
                {loc.title}
              </label>
              <Button
                variant={loc.isDefault ? "primary" : "secondary"}
                onClick={() => save(checked ? enabledIds : [...enabledIds, loc.id], loc.id)}
                disabled={busy !== null}
              >
                {loc.isDefault ? "Default" : "Set default"}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function detectAiPreset(baseUrl: string): AiProviderPresetId {
  const normalized = baseUrl.trim().replace(/\/+$/, "");
  const preset = AI_PROVIDER_PRESETS.find((item) => item.id !== "other" && item.baseUrl === normalized);
  return preset?.id ?? "other";
}

function detectEmbeddingPreset(baseUrl: string): EmbeddingProviderPresetId {
  const normalized = baseUrl.trim().replace(/\/+$/, "");
  const preset = EMBEDDING_PROVIDER_PRESETS.find((item) => item.id !== "other" && item.baseUrl === normalized);
  return preset?.id ?? "other";
}

function AiCredentialSettingsCard() {
  const { pick } = useUiLanguage();
  const [credential, setCredential] = useState<AiCredential | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState<"load" | "save" | "test" | "delete" | null>(null);
  const [presetId, setPresetId] = useState<AiProviderPresetId>("openai");
  const [baseUrl, setBaseUrl] = useState<string>(AI_PROVIDER_PRESETS[0].baseUrl);
  const [model, setModel] = useState<string>(AI_PROVIDER_PRESETS[0].model);
  const [apiKey, setApiKey] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<AiCredentialTestResult | null>(null);

  const load = useCallback(async () => {
    setBusy("load");
    setMessage(null);
    try {
      const data = await aiCredentialApi.get();
      setCredential(data);
      setBaseUrl(data.base_url);
      setModel(data.model);
      setPresetId(detectAiPreset(data.base_url));
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setCredential(null);
      } else {
        setMessage(e instanceof ApiError ? e.message : String(e));
      }
    } finally {
      setLoaded(true);
      setBusy(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function applyPreset(nextId: AiProviderPresetId) {
    setPresetId(nextId);
    const preset = AI_PROVIDER_PRESETS.find((item) => item.id === nextId);
    if (!preset || preset.id === "other") return;
    setBaseUrl(preset.baseUrl);
    setModel(preset.model);
  }

  function validateInput(): string | null {
    if (!baseUrl.trim()) return "Base URL is required.";
    if (!model.trim()) return "Model is required.";
    if (!apiKey.trim()) return "API key is required.";
    return null;
  }

  async function handleTest() {
    const error = validateInput();
    if (error) {
      window.alert(error);
      return;
    }
    setBusy("test");
    setMessage(null);
    setTestResult(null);
    try {
      const result = await aiCredentialApi.test({
        base_url: baseUrl.trim(),
        model: model.trim(),
        api_key: apiKey.trim(),
      });
      setTestResult(result);
      setMessage(pick("接続テストに成功しました。", "Connection test succeeded."));
    } catch (e) {
      setMessage(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function handleSave() {
    const error = validateInput();
    if (error) {
      window.alert(error);
      return;
    }
    setBusy("save");
    setMessage(null);
    try {
      const saved = await aiCredentialApi.upsert({
        base_url: baseUrl.trim(),
        model: model.trim(),
        api_key: apiKey.trim(),
      });
      setCredential(saved);
      setPresetId(detectAiPreset(saved.base_url));
      setApiKey("");
      setTestResult(null);
      setMessage(pick("AI設定を保存しました。", "AI settings saved."));
    } catch (e) {
      setMessage(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function handleDelete() {
    if (!window.confirm(pick("保存済みのAPIキーを削除しますか？", "Delete the saved API key?"))) return;
    setBusy("delete");
    setMessage(null);
    try {
      await aiCredentialApi.delete();
      setCredential(null);
      setApiKey("");
      setTestResult(null);
      setMessage(pick("AI設定を削除しました。", "AI settings deleted."));
    } catch (e) {
      setMessage(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <SettingCard
      icon={<Sparkles size={20} />}
      title={pick("AI連携", "AI Integration")}
      subtitle={pick("会社共通のOpenAI互換APIキー", "Company-wide OpenAI-compatible API key")}
      status={credential ? "connected" : "warning"}
      showDefaultActions={false}
    >
      <div style={{ display: "grid", gap: 14 }}>
        {credential ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
            <div style={{ padding: 12, border: "1px solid #e5e7eb", borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>Provider</div>
              <div style={{ marginTop: 4, fontWeight: 700 }}>{detectAiPreset(credential.base_url) === "other" ? "OpenAI Compatible" : AI_PROVIDER_PRESETS.find((p) => p.id === detectAiPreset(credential.base_url))?.label}</div>
            </div>
            <div style={{ padding: 12, border: "1px solid #e5e7eb", borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>Model</div>
              <div style={{ marginTop: 4, fontWeight: 700 }}>{credential.model}</div>
            </div>
            <div style={{ padding: 12, border: "1px solid #e5e7eb", borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>API Key</div>
              <div style={{ marginTop: 4, fontWeight: 700, color: "#166534" }}>{credential.api_key_hint ?? "設定済み"}</div>
            </div>
          </div>
        ) : null}

        <div className="field">
          <label className="field-label">Provider</label>
          <select
            className="select"
            value={presetId}
            disabled={busy !== null}
            onChange={(e) => applyPreset(e.target.value as AiProviderPresetId)}
          >
            {AI_PROVIDER_PRESETS.map((item) => (
              <option key={item.id} value={item.id}>{item.label}</option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="field-label">Base URL <span className="field-label-required">*</span></label>
          <input
            className="input"
            type="url"
            value={baseUrl}
            disabled={busy !== null}
            onChange={(e) => {
              setBaseUrl(e.target.value);
              setPresetId(detectAiPreset(e.target.value));
            }}
            placeholder="https://api.openai.com"
          />
        </div>

        <div className="field">
          <label className="field-label">Model <span className="field-label-required">*</span></label>
          <input
            className="input"
            value={model}
            disabled={busy !== null}
            onChange={(e) => setModel(e.target.value)}
            placeholder="gpt-4.1-mini"
          />
        </div>

        <div className="field">
          <label className="field-label">API Key <span className="field-label-required">*</span></label>
          <input
            className="input"
            type="password"
            value={apiKey}
            disabled={busy !== null}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={credential?.api_key_hint ? `${credential.api_key_hint} / 新しいキーを入力` : "sk-..."}
            autoComplete="off"
          />
          <span className="field-hint">
            {pick("保存後は完全なキーを表示しません。削除時はDBから物理削除します。", "The full key is never shown after saving. Delete physically removes it from DB.")}
          </span>
        </div>

        {testResult ? (
          <div style={{ padding: "10px 12px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, fontSize: 13, color: "#166534" }}>
            <CheckCircle2 size={16} style={{ verticalAlign: "text-bottom", marginRight: 6 }} />
            {testResult.message}
          </div>
        ) : null}

        {message ? (
          <div style={{ fontSize: 13, color: message.includes("成功") || message.includes("saved") || message.includes("deleted") ? "#166534" : "#b45309" }}>
            {message}
          </div>
        ) : null}

        <hr className="divider" />
        <div className="row gap-8">
          <Button onClick={handleSave} disabled={!loaded || busy !== null} iconLeft={<Save size={13} />}>
            {busy === "save" ? pick("保存中", "Saving") : pick("保存", "Save")}
          </Button>
          <Button variant="secondary" onClick={handleTest} disabled={!loaded || busy !== null}>
            {busy === "test" ? pick("確認中", "Testing") : pick("テスト接続", "Test connection")}
          </Button>
          <Button variant="ghost" onClick={load} disabled={busy !== null} iconLeft={<RefreshCw size={13} />}>
            {pick("再読込", "Reload")}
          </Button>
          {credential ? (
            <Button variant="danger" onClick={handleDelete} disabled={busy !== null} iconLeft={<Trash2 size={13} />}>
              {busy === "delete" ? pick("削除中", "Deleting") : pick("削除", "Delete")}
            </Button>
          ) : null}
        </div>
      </div>
    </SettingCard>
  );
}

function EmbeddingCredentialSettingsCard() {
  const { pick } = useUiLanguage();
  const [credential, setCredential] = useState<AiCredential | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState<"load" | "save" | "test" | "delete" | null>(null);
  const [presetId, setPresetId] = useState<EmbeddingProviderPresetId>("voyage");
  const [baseUrl, setBaseUrl] = useState<string>(EMBEDDING_PROVIDER_PRESETS[0].baseUrl);
  const [model, setModel] = useState<string>(EMBEDDING_PROVIDER_PRESETS[0].model);
  const [apiKey, setApiKey] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<AiCredentialTestResult | null>(null);

  const load = useCallback(async () => {
    setBusy("load");
    setMessage(null);
    try {
      const data = await aiCredentialApi.embedding.get();
      setCredential(data);
      setBaseUrl(data.base_url);
      setModel(data.model);
      setPresetId(detectEmbeddingPreset(data.base_url));
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setCredential(null);
        setBaseUrl(EMBEDDING_PROVIDER_PRESETS[0].baseUrl);
        setModel(EMBEDDING_PROVIDER_PRESETS[0].model);
        setPresetId("voyage");
      } else {
        setMessage(e instanceof ApiError ? e.message : String(e));
      }
    } finally {
      setLoaded(true);
      setBusy(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function applyPreset(nextId: EmbeddingProviderPresetId) {
    setPresetId(nextId);
    const preset = EMBEDDING_PROVIDER_PRESETS.find((item) => item.id === nextId);
    if (!preset || preset.id === "other") return;
    setBaseUrl(preset.baseUrl);
    setModel(preset.model);
  }

  function validateInput(): string | null {
    if (!baseUrl.trim()) return "Base URL is required.";
    if (!model.trim()) return "Model is required.";
    if (!apiKey.trim()) return "API key is required.";
    return null;
  }

  async function handleTest() {
    const error = validateInput();
    if (error) {
      window.alert(error);
      return;
    }
    setBusy("test");
    setMessage(null);
    setTestResult(null);
    try {
      const result = await aiCredentialApi.embedding.test({
        base_url: baseUrl.trim(),
        model: model.trim(),
        api_key: apiKey.trim(),
      });
      setTestResult(result);
      setMessage(pick("Embedding接続テストに成功しました。", "Embedding connection test succeeded."));
    } catch (e) {
      setMessage(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function handleSave() {
    const error = validateInput();
    if (error) {
      window.alert(error);
      return;
    }
    setBusy("save");
    setMessage(null);
    try {
      const saved = await aiCredentialApi.embedding.upsert({
        base_url: baseUrl.trim(),
        model: model.trim(),
        api_key: apiKey.trim(),
      });
      setCredential(saved);
      setPresetId(detectEmbeddingPreset(saved.base_url));
      setApiKey("");
      setTestResult(null);
      setMessage(pick("Embedding設定を保存しました。", "Embedding settings saved."));
    } catch (e) {
      setMessage(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function handleDelete() {
    if (!window.confirm(pick("保存済みのEmbedding APIキーを削除しますか？", "Delete the saved embedding API key?"))) return;
    setBusy("delete");
    setMessage(null);
    try {
      await aiCredentialApi.embedding.delete();
      setCredential(null);
      setApiKey("");
      setTestResult(null);
      setMessage(pick("Embedding設定を削除しました。未設定時は環境変数の既定値を使います。", "Embedding settings deleted. The system default is used when unset."));
    } catch (e) {
      setMessage(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <SettingCard
      icon={<Database size={20} />}
      title={pick("Embedding連携", "Embedding Integration")}
      subtitle={pick("Chatbot知識ベース検索用のVoyage互換APIキー", "Voyage-compatible API key for chatbot retrieval")}
      status={credential ? "connected" : "warning"}
      showDefaultActions={false}
    >
      <div style={{ display: "grid", gap: 14 }}>
        {credential ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
            <div style={{ padding: 12, border: "1px solid #e5e7eb", borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>Provider</div>
              <div style={{ marginTop: 4, fontWeight: 700 }}>
                {detectEmbeddingPreset(credential.base_url) === "other" ? "Voyage Compatible" : "Voyage AI"}
              </div>
            </div>
            <div style={{ padding: 12, border: "1px solid #e5e7eb", borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>Model</div>
              <div style={{ marginTop: 4, fontWeight: 700 }}>{credential.model}</div>
            </div>
            <div style={{ padding: 12, border: "1px solid #e5e7eb", borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>API Key</div>
              <div style={{ marginTop: 4, fontWeight: 700, color: "#166534" }}>{credential.api_key_hint ?? "設定済み"}</div>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 13, color: "#64748b", lineHeight: 1.6 }}>
            {pick("未設定の場合はサーバーの既定Voyage設定を使用します。tenantごとに別キーを使う場合だけ保存してください。", "When unset, the server default Voyage settings are used. Save a tenant-specific key only when needed.")}
          </div>
        )}

        <div className="field">
          <label className="field-label">Provider</label>
          <select
            className="select"
            value={presetId}
            disabled={busy !== null}
            onChange={(e) => applyPreset(e.target.value as EmbeddingProviderPresetId)}
          >
            {EMBEDDING_PROVIDER_PRESETS.map((item) => (
              <option key={item.id} value={item.id}>{item.label}</option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="field-label">Base URL <span className="field-label-required">*</span></label>
          <input
            className="input"
            type="url"
            value={baseUrl}
            disabled={busy !== null}
            onChange={(e) => {
              setBaseUrl(e.target.value);
              setPresetId(detectEmbeddingPreset(e.target.value));
            }}
            placeholder="https://api.voyageai.com/v1"
          />
        </div>

        <div className="field">
          <label className="field-label">Model <span className="field-label-required">*</span></label>
          <input
            className="input"
            value={model}
            disabled={busy !== null}
            onChange={(e) => setModel(e.target.value)}
            placeholder="voyage-3-lite"
          />
        </div>

        <div className="field">
          <label className="field-label">API Key <span className="field-label-required">*</span></label>
          <input
            className="input"
            type="password"
            value={apiKey}
            disabled={busy !== null}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={credential?.api_key_hint ? `${credential.api_key_hint} / 新しいキーを入力` : "pa-..."}
            autoComplete="off"
          />
        </div>

        {testResult ? (
          <div style={{ padding: "10px 12px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, fontSize: 13, color: "#166534" }}>
            <CheckCircle2 size={16} style={{ verticalAlign: "text-bottom", marginRight: 6 }} />
            {testResult.message}
          </div>
        ) : null}

        {message ? (
          <div style={{ fontSize: 13, color: message.includes("成功") || message.includes("saved") || message.includes("deleted") ? "#166534" : "#b45309" }}>
            {message}
          </div>
        ) : null}

        <hr className="divider" />
        <div className="row gap-8">
          <Button onClick={handleSave} disabled={!loaded || busy !== null} iconLeft={<Save size={13} />}>
            {busy === "save" ? pick("保存中", "Saving") : pick("保存", "Save")}
          </Button>
          <Button variant="secondary" onClick={handleTest} disabled={!loaded || busy !== null}>
            {busy === "test" ? pick("確認中", "Testing") : pick("テスト接続", "Test connection")}
          </Button>
          <Button variant="ghost" onClick={load} disabled={busy !== null} iconLeft={<RefreshCw size={13} />}>
            {pick("再読込", "Reload")}
          </Button>
          {credential ? (
            <Button variant="danger" onClick={handleDelete} disabled={busy !== null} iconLeft={<Trash2 size={13} />}>
              {busy === "delete" ? pick("削除中", "Deleting") : pick("削除", "Delete")}
            </Button>
          ) : null}
        </div>
      </div>
    </SettingCard>
  );
}

/**
 * システム設定ページ
 *
 * Stage 3C 权限策略:
 * - system_admin: 看到 WordPressSettingsCard + 5 个 mock card + SessionsPanel
 * - editor / viewer: 看到「現在設定できる項目はありません」占位 + SessionsPanel
 *
 * WordPressSettingsCard 整块不渲染(不触发 403 红框)。
 */
export const SettingsPage = () => {
  const { isSystemAdmin } = useAuth();
  const { pick } = useUiLanguage();

  if (!isSystemAdmin) {
    return (
      <div className="settings-grid">
        <LanguageSettingsCard />
        <PasswordChangeCard />
        <MailSettingsCard allowedAccountTypes={["personal"]} />
        <SessionsPanel />
      </div>
    );
  }

  return (
    <div className="settings-grid">
      <LanguageSettingsCard />

      <SettingCard
        icon={<Instagram size={20} />}
        title={pick("Meta・Instagram 設定", "Meta / Instagram Settings")}
        subtitle={pick("投稿・コメント・DM管理", "Posts, comments, and DM management")}
        status="warning"
        showDefaultActions={false}
      >
        <MetaSettingsCardInner />
      </SettingCard>

      {/* 1. WordPress 接続設定 — Stage 3B 真实化,仅 system_admin */}
      <WordPressSettingsCard />

      {/* 2. Mail 連携 — Stage Mail-1 真实化,仅 system_admin */}
      <MailSettingsCard />

      {/* 3-6. 他の設定:Stage 1 mock だが、Stage 3C 收尾で fake 内容削除。
         現時点では WordPress 接続設定 + Mail 連携 が実機能。
         残りの Card は タイトル / 説明を残し「今後対応予定」のみ表示。 */}

      <AiCredentialSettingsCard />
      <EmbeddingCredentialSettingsCard />

      <SettingCard
        icon={<BarChart3 size={20} />}
        title="Google Analytics 設定"
        subtitle="アクセス解析データの取得"
        status="warning"
      >
        <GoogleAnalyticsSettingsCardInner />
      </SettingCard>

      <SettingCard
        icon={<MapPin size={20} />}
        title="Googleビジネス設定"
        subtitle="口コミ・店舗情報の管理"
        status="warning"
        showDefaultActions={false}
      >
        <GoogleBusinessSettingsCardInner />
      </SettingCard>

      <PasswordChangeCard />
      <SessionsPanel />
    </div>
  );
};
